import { ComponentType } from '@angular/cdk/portal';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { TreeItemStatus } from '@core/enums/tree-item-status';
import { AssignTaskInspectorModalInput } from '@core/interfaces/modal-inputs/assign-task-inspector-modal-input';
import { RejectReportModalInput } from '@core/interfaces/modal-inputs/reject-report-modal-input';
import { TenantUser } from '@core/interfaces/tenant-user';
import { WorkAssignmentItem } from '@core/interfaces/work-assignment-item';
import { AssignmentService } from '@web/services/assignment.service';
import { CacheService } from '@core/services/cache.service';
import { TenantService } from '@core/services/tenant.service';
import { WorkAssignmentService } from '@core/services/work-assignment.service';
import { RejectReport } from '@core/store/actions/my-items.action';
import { selectUsers } from '@core/store/selectors/data.selector';
import { Store } from '@ngrx/store';
import { AssignTaskInspectorDialogComponent } from '@shared/dialogs/assign-task-inspector-dialog/assign-task-inspector-dialog.component';
import { LoggerService } from '@shared/services/logger.service';
import { DialogService, FormDialogBaseComponent, SnackBarService } from 'osc-reference-app-common';
import { first, from, of, Subject, switchMap } from 'rxjs';
import { AppState } from 'src/app/app.state';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { RejectInspectionReportRequest } from '@core/interfaces/requests/reject-inspection-report-request';
import { UserProfile } from '@osc-utils/models/user-profile';
import { CurrentUserService } from '@core/services/current-user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InspectionApprovalService } from '../../services/inspection-approval.service';

@Component({
  selector: 'app-reject-report-modal',
  templateUrl: './reject-report-modal.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatInput,
    MatFormField,
    MatLabel,
    MatHint,
    MatError,
    MatDialogActions,
    MatButton,
    NgClass,
    TranslateModule,
  ],
})
export class RejectReportModalComponent
  extends FormDialogBaseComponent<RejectReportModalComponent, RejectReportModalInput, string>
  implements OnInit
{
  #inspectionApprovalService = inject(InspectionApprovalService);
  #_store = inject<Store<AppState>>(Store);
  #snackbarService = inject(SnackBarService);
  #workAssignmentService = inject(WorkAssignmentService);
  #cacheService = inject(CacheService);
  #tenantService = inject(TenantService);
  #dialogService = inject(DialogService);
  #assignmentService = inject(AssignmentService);
  #logger = inject(LoggerService);
  #currentUserService = inject(CurrentUserService);

  public selectedTasksAssignments: WorkAssignmentItem[];
  public onSaving: boolean;
  public workAssignmentsSubject: Subject<void> = new Subject<void>();
  public taskAssignmentsSubject: Subject<void> = new Subject<void>();
  #tenantUsers: TenantUser[];
  #currentUser: UserProfile;
  #destroyRef = inject(DestroyRef);

  public constructor() {
    const dialogRef = inject<MatDialogRef<RejectReportModalComponent, string>>(MatDialogRef);
    const data = inject<RejectReportModalInput>(MAT_DIALOG_DATA);

    super(dialogRef, data);
  }

  public async ngOnInit() {
    this.buildForm(this.data);
    this.workAssignmentsSubject.asObservable().subscribe(() => {
      this.taskAssignmentsSubject.next();
    });
    this.#tenantUsers = await this.#cacheService.get<TenantUser>(
      () => this.#tenantService.getTenantUsers(),
      this.#_store.select(selectUsers).pipe(first()),
    );
    this.#currentUserService.currentUser$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(user => {
      this.#currentUser = user;
    });
  }

  public reject(): void {
    if (this.form.valid) {
      this.onSaving = true;

      const request: RejectInspectionReportRequest = {
        inspectionReportApprovalId: this.data.reportId,
        comment: this.form.controls.reason.value,
        approverId: this.#currentUser.id,
      };

      this.#inspectionApprovalService
        .rejectInspection(request)
        .then(() => {
          this.dialogRef.close(this.form.controls.reason.value);
          this.#_store.dispatch(RejectReport({ reportId: this.data.reportId }));
          this.onSaving = false;
          this.reAssign();
        })
        .catch(err => {
          this.#logger.error('Reject report error', err);
          this.onSaving = false;
          this.#snackbarService.error(`There has been an error during rejecting report`);
        });
    }
  }

  public reAssign(): void {
    this.#workAssignmentService
      .getLatestWorkAssignment()
      .pipe(
        first(),
        switchMap(workAssignments =>
          workAssignments !== null
            ? of(workAssignments)
            : from(this.#workAssignmentService.getWorkAssignment(this.data.projectId).then(r => r.items)),
        ),
      )
      .subscribe(workAssignments => {
        const selectedTasksAssignments = workAssignments.find(w => w.taskId === this.data.taskId);
        const input: AssignTaskInspectorModalInput = {
          workAssignmentItems: [selectedTasksAssignments],
          users: this.#tenantUsers,
          projectId: this.data.projectId,
          isReAssign: true,
        };
        const dialogRef: MatDialogRef<
          AssignTaskInspectorDialogComponent,
          { selectedTaskInspectorId: string; reportApprovals: Array<{ taskId: string; reportId: string; approver: TenantUser }> }
        > = this.#dialogService.openDialog(AssignTaskInspectorDialogComponent as ComponentType<AssignTaskInspectorDialogComponent>, input);
        dialogRef
          .afterClosed()
          .subscribe(
            (result: {
              selectedTaskInspectorId: string;
              reportApprovals: Array<{ taskId: string; reportId: string; approver: TenantUser }>;
            }) => {
              if (result) {
                this.#assignmentService.getProjectAssignments(this.data.projectId).then(() => {
                  const items = workAssignments.map(item => {
                    const workAssignment = input.workAssignmentItems.find(w => w.taskId === item.taskId);
                    if (workAssignment) {
                      const reportApprovals = result.reportApprovals.find(r => r.taskId === item.taskId);
                      item.taskInspector = input.users.find(u => u.id === result.selectedTaskInspectorId);
                      item.approver = reportApprovals.approver;
                      item.reportApprovalId = reportApprovals.reportId;
                      item.taskStatus = item.taskStatus === TreeItemStatus.Planning ? TreeItemStatus.Deployed : item.taskStatus;
                    }
                    return item;
                  });
                  this.#workAssignmentService.workAssignmentSelectionChanged([]);
                  this.#workAssignmentService.workAssignmentChanged(items);
                  this.workAssignmentsSubject.next();
                });
              }
            },
          );
      });
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  protected buildForm(initialData?: RejectReportModalInput): FormGroup {
    this.form = new FormGroup({
      id: new FormControl<string>(this.data.reportId, { nonNullable: true, validators: [Validators.required] }),
      reason: new FormControl<string>(this.data.reason, { nonNullable: true, validators: [Validators.required] }),
    });
    return this.form;
  }
}
